import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, 
  TrendingUp, 
  Activity, 
  DollarSign, 
  Eye,
  ExternalLink,
  Clock,
  Zap,
  Target,
  PieChart,
  LineChart,
  AlertTriangle
} from 'lucide-react';
import Hero from '../components/Hero';
import { ToastProvider } from '../components/ui/Toast';




// Import dashboard components
import SimpleFundingRate from '../components/SimpleFundingRate';
import FundingRatesGrid from '../components/FundingRatesGrid';
import FearGreedIndex from '../components/FearGreedIndex';
import OpenInterestChart from '../components/OpenInterestChart';
import LongShortRatio from '../components/LongShortRatio';
import Liquidations from '../components/Liquidations';
import FundingRateHeatmap from '../components/FundingRateHeatmap';
import AdvancedTradingDashboard from '../components/AdvancedTradingDashboard';
import LiquidationsHeatmap from '../components/LiquidationsHeatmap';
import MarketSentimentAnalyzer from '../components/MarketSentimentAnalyzer';

interface DashboardTile {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  component?: React.ComponentType;
  route?: string;
  apiCalls: number;
  updateFrequency: string;
}

const dashboardTiles: DashboardTile[] = [
  {
    id: 'funding-rates',
    title: 'Funding Rates',
    description: 'Real-time funding rates across exchanges',
    icon: <DollarSign className="w-6 h-6" />,
    color: 'from-yellow-500 to-orange-500',
    component: FundingRatesGrid,
    route: '/funding-rates',
    apiCalls: 1,
    updateFrequency: '2 min'
  },
  {
    id: 'simple-funding',
    title: 'Quick Funding View',
    description: 'Simplified funding rates display',
    icon: <Zap className="w-6 h-6" />,
    color: 'from-amber-500 to-yellow-500',
    component: SimpleFundingRate,
    apiCalls: 1,
    updateFrequency: '5 min'
  },
  {
    id: 'fear-greed',
    title: 'Fear & Greed Index',
    description: 'Market sentiment indicator',
    icon: <Activity className="w-6 h-6" />,
    color: 'from-purple-500 to-pink-500',
    component: FearGreedIndex,
    apiCalls: 1,
    updateFrequency: '1 hour'
  },
  {
    id: 'open-interest',
    title: 'Open Interest',
    description: 'Track market exposure and positions',
    icon: <BarChart3 className="w-6 h-6" />,
    color: 'from-blue-500 to-cyan-500',
    component: OpenInterestChart,
    apiCalls: 1,
    updateFrequency: '5 min'
  },
  {
    id: 'long-short',
    title: 'Long/Short Ratio',
    description: 'Market positioning analysis',
    icon: <TrendingUp className="w-6 h-6" />,
    color: 'from-green-500 to-emerald-500',
    component: LongShortRatio,
    apiCalls: 1,
    updateFrequency: '5 min'
  },
  {
    id: 'liquidations',
    title: 'Liquidations',
    description: '24h liquidation data',
    icon: <AlertTriangle className="w-6 h-6" />,
    color: 'from-red-500 to-rose-500',
    component: Liquidations,
    apiCalls: 1,
    updateFrequency: '1 min'
  },  {
    id: 'funding-heatmap',
    title: 'Funding Heatmap',
    description: 'Visual funding rate comparison',
    icon: <PieChart className="w-6 h-6" />,
    color: 'from-indigo-500 to-purple-500',
    component: FundingRateHeatmap,
    apiCalls: 1,
    updateFrequency: '5 min'
  },
  {
    id: 'advanced-dashboard',
    title: 'Advanced Analytics',
    description: 'Comprehensive trading dashboard',
    icon: <LineChart className="w-6 h-6" />,
    color: 'from-cyan-500 to-blue-500',
    component: AdvancedTradingDashboard,
    apiCalls: 4,
    updateFrequency: '30 sec'
  },
  {
    id: 'liquidations-heatmap',
    title: 'Liquidations Heatmap',
    description: 'Visual liquidation activity map',
    icon: <Target className="w-6 h-6" />,
    color: 'from-rose-500 to-red-500',
    component: LiquidationsHeatmap,
    apiCalls: 1,
    updateFrequency: '1 min'
  },
  {
    id: 'sentiment-analyzer',
    title: 'Market Sentiment',
    description: 'AI-powered sentiment analysis',
    icon: <Activity className="w-6 h-6" />,
    color: 'from-purple-500 to-violet-500',
    component: MarketSentimentAnalyzer,
    apiCalls: 4,
    updateFrequency: '1 min'
  }
];

const DashboardTileComponent: React.FC<{ 
  tile: DashboardTile; 
  isHovered: boolean;
  onHover: (id: string | null) => void;
  onClick: (id: string) => void;
  isSelected: boolean;
}> = ({ tile, isHovered, onHover, onClick, isSelected }) => {
  return (
    <motion.div
      className={`relative group cursor-pointer overflow-hidden rounded-xl border transition-all duration-300 ${
        isSelected 
          ? 'border-yellow-400 bg-zinc-800/80' 
          : 'border-zinc-800 bg-zinc-900 hover:border-zinc-700 hover:bg-zinc-800/50'
      }`}
      onMouseEnter={() => onHover(tile.id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onClick(tile.id)}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${tile.color} opacity-10 group-hover:opacity-20 transition-opacity`} />
      
      <div className="relative p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-lg bg-gradient-to-br ${tile.color} text-white`}>
            {tile.icon}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Clock className="w-3 h-3" />
            {tile.updateFrequency}
          </div>
        </div>

        {/* Content */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-white mb-2">{tile.title}</h3>
          <p className="text-sm text-gray-400 leading-relaxed">{tile.description}</p>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1 text-gray-500">
            <Zap className="w-3 h-3" />
            {tile.apiCalls} API call{tile.apiCalls > 1 ? 's' : ''}
          </div>
          {tile.route && (
            <div className="flex items-center gap-1 text-gray-500 group-hover:text-yellow-400 transition-colors">
              <ExternalLink className="w-3 h-3" />
              Open page
            </div>
          )}
        </div>

        {/* Hover overlay */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center"
            >
              <div className="text-center">
                <Eye className="w-8 h-8 text-white mx-auto mb-2" />
                <p className="text-white text-sm font-medium">View Dashboard</p>
                {tile.route && (
                  <p className="text-gray-300 text-xs mt-1">or click to open full page</p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const DashboardPage: React.FC = () => {
  const [hoveredTile, setHoveredTile] = useState<string | null>(null);
  const [selectedTile, setSelectedTile] = useState<string>('funding-rates');
  const [showDashboard, setShowDashboard] = useState(false);

  useEffect(() => {
    document.title = 'Crypto Dashboard | CryptoIndia';
  }, []);

  const handleTileClick = (tileId: string) => {
    const tile = dashboardTiles.find(t => t.id === tileId);
    if (tile?.route) {
      // Navigate to dedicated page
      window.location.href = tile.route;
    } else {
      // Show in dashboard
      setSelectedTile(tileId);
      setShowDashboard(true);
    }
  };

  const selectedTileData = dashboardTiles.find(t => t.id === selectedTile);

  return (
    <div className="min-h-screen bg-black">
      <Hero 
        title="Crypto Market Dashboard"
        subtitle="Real-time market data and analytics at your fingertips"
        config={{
          image: "https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg",
          overlayOpacity: 0.8,
          position: "center"
        }}
      />
      <ToastProvider>
        <section className="py-8 md:py-12">
          <div className="container mx-auto px-4">
            {/* Dashboard Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Market Tools</h2>
                  <p className="text-gray-400 text-sm md:text-base">
                    Hover to preview • Click to open dedicated pages
                  </p>
                </div>
                <div className="text-right">
                  
                  
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {dashboardTiles.map((tile) => (
                  <DashboardTileComponent
                    key={tile.id}
                    tile={tile}
                    isHovered={hoveredTile === tile.id}
                    onHover={setHoveredTile}
                    onClick={handleTileClick}
                    isSelected={selectedTile === tile.id && showDashboard}
                  />
                ))}
              </div>
            </motion.div>

            {/* Dashboard Display */}
            <AnimatePresence>
              {showDashboard && selectedTileData?.component && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="mt-12"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg bg-gradient-to-br ${selectedTileData.color} text-white`}>
                        {selectedTileData.icon}
                      </div>
                      <div>
                        <h3 className="text-xl md:text-2xl font-bold text-white">
                          {selectedTileData.title}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {selectedTileData.description}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowDashboard(false)}
                      className="px-4 py-2 bg-zinc-800 text-gray-300 rounded-lg hover:bg-zinc-700 transition-colors text-sm"
                    >
                      Close
                    </button>
                  </div>

                  <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-1">
                    <selectedTileData.component />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      </ToastProvider>
    </div>
  );
};

export default DashboardPage;
